import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type AsyncValue<T> = {
  loading: boolean;
  value?: T;
  error?: Error;
};

/**
 * Changing this value re-runs every useAsyncValue below it. The last value
 * stays while the new run loads, so a refresh doesn't blank the page.
 */
const AsyncRefreshContext = createContext(0);
const RefreshNowContext = createContext<() => void>(() => undefined);

/** Reloads every useAsyncValue inside the nearest RefreshOnFocus now, for a
 * Retry button. Outside one it does nothing. */
export const useRefreshNow = () => useContext(RefreshNowContext);

/**
 * Runs `load` whenever it changes (memoize it with useCallback) and tracks its
 * result, ignoring answers from runs that a newer one has replaced.
 */
export function useAsyncValue<T>(load: () => Promise<T>): AsyncValue<T> {
  const refresh = useContext(AsyncRefreshContext);
  const [state, setState] = useState<AsyncValue<T>>({ loading: true });
  useEffect(() => {
    let current = true;
    setState(prev => ({ ...prev, loading: true }));
    load().then(
      value => {
        if (current) setState({ loading: false, value });
      },
      error => {
        if (current) setState({ loading: false, error });
      },
    );
    return () => {
      current = false;
    };
  }, [load, refresh]);
  return state;
}

/** Don't refresh more often than this, however often the window gets focus. */
export const REFRESH_AFTER_MS = 2 * 60_000;

/**
 * Re-runs the useAsyncValue hooks inside it when someone comes back to the
 * window (focus, or the browser tab becoming visible again), at most once
 * per REFRESH_AFTER_MS. A page left open for hours then catches up as soon
 * as it's looked at, without polling in the background.
 */
export const RefreshOnFocus = ({ children }: { children: ReactNode }) => {
  const [refresh, setRefresh] = useState(0);
  const last = useRef(Date.now());
  const refreshNow = useCallback(() => {
    last.current = Date.now();
    setRefresh(value => value + 1);
  }, []);
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - last.current < REFRESH_AFTER_MS) return;
      last.current = Date.now();
      setRefresh(value => value + 1);
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, []);
  return createElement(
    RefreshNowContext.Provider,
    { value: refreshNow },
    createElement(AsyncRefreshContext.Provider, { value: refresh }, children),
  );
};
